/**
 * Rolling-stock register client — /api/research/locos (app.StudioLoco, db/83).
 * Thin fetch helpers; the register is small, so the list is the whole thing.
 */
import type { FleetLoco } from './types'

const JSON_HEADERS = { 'content-type': 'application/json' }

async function readJson(r: Response): Promise<Record<string, unknown>> {
  return (await r.json().catch(() => ({}))) as Record<string, unknown>
}
function fail(data: Record<string, unknown>, r: Response, what: string): Error {
  return new Error(typeof data.error === 'string' ? data.error : `${what} failed (${r.status})`)
}

export async function listLocos(): Promise<FleetLoco[]> {
  const r = await fetch('/api/research/locos')
  const data = await readJson(r)
  if (!r.ok) throw fail(data, r, 'load the rolling-stock register')
  return (data.locos as FleetLoco[]) ?? []
}

/** Everything but the server-set fields. */
export type LocoInput = Omit<FleetLoco, 'id' | 'updatedBy' | 'updatedAt'>

export async function createLoco(input: LocoInput): Promise<number> {
  const r = await fetch('/api/research/locos', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(input) })
  const data = await readJson(r)
  if (!r.ok) throw fail(data, r, 'add the engine')
  return data.id as number
}

export async function updateLoco(id: number, input: LocoInput): Promise<void> {
  const r = await fetch(`/api/research/locos/${id}`, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(input) })
  if (!r.ok) throw fail(await readJson(r), r, 'save the engine')
}

export async function deleteLoco(id: number): Promise<void> {
  const r = await fetch(`/api/research/locos/${id}`, { method: 'DELETE' })
  if (!r.ok && r.status !== 204) throw fail(await readJson(r), r, 'remove the engine')
}

/** A blank engine for the "Add an engine" row. */
export const blankLoco = (): LocoInput => ({
  number: '', name: null, owner: null, ownership: 'contract', traction: 'steam', fuel: 'coal', seats: null,
  whitbyEnabled: false, costPerMile: null, dayRate: null, fixedAnnualCost: null, maintenancePerMile: null, fuelPerMile: null,
  minContractMiles: null, basisMiles: null, failRate: null, activeFrom: null, activeTo: null,
  washoutSeed: null, washoutCycleDays: 28, washoutLockoutDays: 7, boilerCertExpiry: null, examNotes: null,
  nr: {}, inSimulation: true, notes: null, sortOrder: 0, unavailable: [],
})
